import { useDriveArchiveStateQuery } from '@/hooks/queries/use-drive-archive-state-query';
import { Box, VisuallyHidden } from '@chakra-ui/react';
import { MentionDriveArchiveStatus } from '@fluenceur/types';
import { Cloud } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface MentionSyncIconProps {
  mentionId: string;
}

/**
 * Tiny "archived to Drive" indicator overlaid on the bottom-right of a mention thumbnail.
 *
 * Hidden by default; the parent element must use `role="group"` so this badge fades in
 * on hover via Chakra's `_groupHover` selector.
 */
export const MentionSyncIcon: React.FC<MentionSyncIconProps> = ({ mentionId }) => {
  const { t } = useTranslation();
  const { data: archiveState } = useDriveArchiveStateQuery(mentionId);

  if (archiveState?.status !== MentionDriveArchiveStatus.ARCHIVED) {
    return null;
  }

  const label = t('mentions.archive.button.archived');

  return (
    <Box
      position="absolute"
      bottom={1.5}
      right={1.5}
      px={1.5}
      py={1}
      bg="whiteAlpha.900"
      color="gray.600"
      rounded="md"
      opacity={0}
      transition="opacity 0.15s"
      _groupHover={{ opacity: 1 }}
      pointerEvents="none"
      data-testid="mention-sync-icon"
    >
      <Cloud width={12} height={12} />
      <VisuallyHidden>{label}</VisuallyHidden>
    </Box>
  );
};
